import { gqlMutate } from '@hapana/shared/web/services/requests.service'
import type { z } from 'zod'
import type { PolicyEquipmentScope } from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'
import type {
  PendingItemsHandoffItem,
  PendingItemsHandoffView,
  PendingItemsRecipient,
} from '@hapana/shared/schemas/policy-compliance/pending-items-handoff-schemas'
import type { createPendingItemsHandoffInputSchema } from '@hapana/shared/schemas/policy-compliance/pending-items-public-schemas'

import type { ReadinessDimensionRow, ReadinessItemRow } from './policy-compliance.service'
import type { GraphqlWireShape } from './wire-shape.types'
import {
  activePendingItemsHandoffsQuery,
  pendingItemsRecipientsQuery,
  cancelPendingItemsHandoffMutation,
  createPendingItemsHandoffMutation,
  equipmentReadinessBatchQuery,
  equipmentReadinessQuery,
  pendingItemsHandoffQuery,
} from './graphql/pending-items-handoff.queries'

export type EquipmentReadinessRow = {
  siteId: string
  clientId: string
  equipmentId: string | null
  equipmentCategory: PolicyEquipmentScope | null
  evaluatedSiteLocalDate: string
  age: ReadinessDimensionRow
  policy: ReadinessDimensionRow
  skinType: ReadinessDimensionRow
  operational: ReadinessDimensionRow
  blockingItems: ReadinessItemRow[]
  actionableItems: ReadinessItemRow[]
  upcomingItems: ReadinessItemRow[]
}

export type PendingItemsHandoffItemRow = GraphqlWireShape<PendingItemsHandoffItem>
export type PendingItemsHandoffRow = Omit<
  GraphqlWireShape<PendingItemsHandoffView>,
  'presenceBinding'
> & { presenceBinding?: string | null }
export type PendingItemsRecipientRow = GraphqlWireShape<PendingItemsRecipient>
export type CreatePendingItemsHandoffVariables = z.input<
  typeof createPendingItemsHandoffInputSchema
>

const runRequest = async <TResponse>(
  document: string,
  variables: Record<string, unknown>
): Promise<TResponse> => (await gqlMutate({ mutation: document, variables })) as TResponse

export const fetchEquipmentReadiness = async (variables: {
  siteId: string
  clientId: string
  equipmentId: string
}): Promise<EquipmentReadinessRow> =>
  (
    await runRequest<{ equipmentReadiness: EquipmentReadinessRow }>(
      equipmentReadinessQuery,
      variables
    )
  ).equipmentReadiness

export type EquipmentReadinessRequest = { clientId: string; equipmentId: string }

export const fetchEquipmentReadinessBatch = async (variables: {
  siteId: string
  requests: EquipmentReadinessRequest[]
}): Promise<EquipmentReadinessRow[]> =>
  (
    await runRequest<{ equipmentReadinessBatch: EquipmentReadinessRow[] }>(
      equipmentReadinessBatchQuery,
      variables
    )
  ).equipmentReadinessBatch

export const createPendingItemsHandoff = async (
  variables: CreatePendingItemsHandoffVariables
): Promise<PendingItemsHandoffRow> =>
  (
    await runRequest<{ createPendingItemsHandoff: PendingItemsHandoffRow }>(
      createPendingItemsHandoffMutation,
      variables
    )
  ).createPendingItemsHandoff

export const cancelPendingItemsHandoff = async (variables: {
  siteId: string
  handoffId: string
}): Promise<PendingItemsHandoffRow> =>
  (
    await runRequest<{ cancelPendingItemsHandoff: PendingItemsHandoffRow }>(
      cancelPendingItemsHandoffMutation,
      variables
    )
  ).cancelPendingItemsHandoff

export const fetchPendingItemsHandoff = async (
  handoffId: string
): Promise<PendingItemsHandoffRow> =>
  (
    await runRequest<{ pendingItemsHandoff: PendingItemsHandoffRow }>(pendingItemsHandoffQuery, {
      handoffId,
    })
  ).pendingItemsHandoff

export const fetchActivePendingItemsHandoffs = async (variables: {
  siteId: string
  clientId: string
}): Promise<PendingItemsHandoffRow[]> =>
  (
    await runRequest<{ activePendingItemsHandoffs: PendingItemsHandoffRow[] }>(
      activePendingItemsHandoffsQuery,
      variables
    )
  ).activePendingItemsHandoffs

export const fetchPendingItemsRecipients = async (variables: {
  siteId: string
  clientId: string
}): Promise<PendingItemsRecipientRow[]> =>
  (
    await runRequest<{ pendingItemsRecipients: PendingItemsRecipientRow[] }>(
      pendingItemsRecipientsQuery,
      variables
    )
  ).pendingItemsRecipients
